import { Alert, Box, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/system';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import LineChart from 'src/components/HighChart/LineChart';

import { Point } from 'highcharts';
import { every, get, isEmpty, omit } from 'lodash';
import { useTranslation } from 'react-i18next';
import FlagCountry from 'src/components/common/FlagCountry';
import WhiteBoxContent from 'src/components/common/WhiteBoxContent';
import WorldMapChart from 'src/components/HighChart/WorldMapChart';
import { WorldMapOptions } from 'src/components/HighChart/WorldMapChart/config';
import LinkToCompanyDetail from 'src/components/LinkToCompanyDetail';
import ModalComponent from 'src/components/Modal/ModalComponent';
import Table from 'src/components/Table';
import FilterChart from 'src/features/TradeData/Common/FilterChart';
import useCreditFlow from 'src/hooks/useCreditFlow';
import { useQueryParams } from 'src/hooks/useQueryParam';
import {
  useQueryGetTradeDataMarketAnalyticBuyers,
  useQueryGetTradeDataMarketAnalyticsAIExplanation,
  useQueryGetTradeDataMarketAnalyticsChart,
  useQueryGetTradeDataMarketAnalyticsTable,
  useQueryGetTradeDataMarketAnalyticSuppliers,
  useQueryGetTradeDataMarketAnalyticsWorldMap,
} from 'src/services/queries/useQueryTradeData';
import { FORMAT_DATE_TIME, TABLE_PAGE_SIZE_12 } from 'src/utils/constants/config';
import { KeyFilterChartTradeDataUnitBy, TypeBuyerSupplier } from 'src/utils/constants/enum';
import getEmojiFlag from 'src/utils/helpers/emojiFlag';
import { renderNumberOrDefault, renderValueNumberTrade } from 'src/utils/helpers/stringHelpers';
import {
  IGetTradeDataCompanyDetailTable,
  IGetTradeDataMarketAnalytics,
  ISearchGlobalTradeDataQueryParams,
} from 'src/utils/typings/dto/tradeData';

import { useTradeDataSortStore } from 'src/zustand/useTradeDataSortStore';

import { AutoAwesome } from '@mui/icons-material';
import { AlertCircle, Calendar, Target, TrendingUp } from 'react-feather';
import AISummaryLoading from 'src/components/common/AISummaryLoading';
import IconFeather from 'src/components/common/Icon';
import TooltipFormLabel from 'src/components/common/TooltipFormLabel';
import TooltipTradeDataLimit from 'src/components/common/TooltipTradeDataLimit';
import ExportDataTable from 'src/components/ExportData';
import { EXPORT_TABLE_TYPE, URL_EXPORT_MAPPING } from 'src/components/ExportData/config';
import CompanyInfoNoData from 'src/features/CompanyDetail/Common/CompanyInfoNoData';
import { TradeDataUnitByFilter } from 'src/features/TradeData/Common/TradeDataUnitbyFilter';
import { LIST_RESTRICTION_DATA, useProvisionRestriction } from 'src/hooks/useProvisionRestriction';
import { LIST_ACTIONS, LIST_FEATURES } from 'src/utils/constants/featuresPoint';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { formatValueUSD } from 'src/utils/helpers/utilities';

const MarketAnalytics = () => {
  const {
    palette: { text, background, primary },
    shape: { borderRadius },
    spacing,
  } = useTheme();
  const { checkRestriction } = useProvisionRestriction();

  const { hs_code, start_date, end_date } = useQueryParams();

  const { t } = useTranslation();
  const queryParams = useQueryParams() as ISearchGlobalTradeDataQueryParams;

  const { setSort: setTableSort, marketAnalyticsTableSortParams } = useTradeDataSortStore();
  const { setSort: setPopupTableSort, marketAnalyticsPopupTableSortParams } = useTradeDataSortStore();

  const [marketAnalyticsTablePaging, setMarketAnalyticsTablePagingModel] = useState({
    page: 0,
    page_size: TABLE_PAGE_SIZE_12,
  });

  const [marketAnalyticsCompanyTablePaging, setMarketAnalyticsCompanyTablePaging] = useState({
    page: 0,
    page_size: 10,
  });
  const [selectedRecord, setSelectedRecord] = useState(undefined as undefined | IMarketAnalytics);

  const tradeDataUnitByFilter = useMemo(() => new TradeDataUnitByFilter(), []);
  const [unitFilter, setUnitFilter] = useState<KeyFilterChartTradeDataUnitBy>(tradeDataUnitByFilter.value);
  const [selectedRangeTime, setSelectedRangeTime] = useState({
    fromTime: queryParams.start_date,
    toTime: queryParams.end_date,
  } as {
    fromTime: string;
    toTime: string;
  });

  const unitFieldMapping = {
    bol: {
      field: 'number_of_bol',
      label: 'Number of B/L',
    },
    weight: {
      field: 'weight',
      label: 'Weight',
    },
    quantity: { field: 'quantity', label: 'Quantity' },
    value_usd: { field: 'value', label: 'USD' },
  };

  const marketAnalyticsCommonRequestPayload: IGetTradeDataMarketAnalytics = useMemo(() => {
    return {
      ...omit(queryParams, [
        //
      ]),
      start_date: selectedRangeTime.fromTime,
      end_date: selectedRangeTime.toTime,
      offset: marketAnalyticsTablePaging.page,
      limit: marketAnalyticsTablePaging.page_size,
    };
  }, [queryParams, marketAnalyticsTablePaging, selectedRangeTime]);

  const marketAnalyticsTableRequestPayload: IGetTradeDataMarketAnalytics = useMemo(() => {
    return {
      ...queryParams,
      start_date: selectedRangeTime.fromTime,
      end_date: selectedRangeTime.toTime,
      offset: marketAnalyticsTablePaging.page,
      limit: marketAnalyticsTablePaging.page_size,
      sort_by: marketAnalyticsTableSortParams?.sortBy,
      order: marketAnalyticsTableSortParams?.sort,
    };
  }, [queryParams, marketAnalyticsTablePaging, selectedRangeTime]);
  const chartLabelByUnitFilter = unitFieldMapping[unitFilter].label;
  const chartFieldNameByUnitFilter = unitFieldMapping[unitFilter].field;
  const isViewingGlobalData = !(
    marketAnalyticsCommonRequestPayload.imp_country_code || marketAnalyticsCommonRequestPayload.exp_country_code
  );

  const [detailTableViewType, setDetailTableViewType] = useState<TypeBuyerSupplier>();

  const [marketAnalyticsCompanyRequestPayload, setMarketAnalyticsCompanyRequestPayload] =
    useState<IGetTradeDataCompanyDetailTable>();
  const [acceptCreditPayment, setAcceptCreditPayment] = useState(true);

  const marketAnalyticsChartResp = useQueryGetTradeDataMarketAnalyticsChart(
    acceptCreditPayment ? marketAnalyticsCommonRequestPayload : undefined,
  );

  // Waiting API get list finished
  const marketAnalyticsChartAIResp = useQueryGetTradeDataMarketAnalyticsAIExplanation(
    !marketAnalyticsChartResp?.isFetching ? marketAnalyticsCommonRequestPayload : undefined,
  );
  const marketAnalyticsTableResp = useQueryGetTradeDataMarketAnalyticsTable(
    acceptCreditPayment ? marketAnalyticsTableRequestPayload : undefined,
  );

  const marketAnalyticsWorldMapResp = useQueryGetTradeDataMarketAnalyticsWorldMap(
    acceptCreditPayment ? marketAnalyticsCommonRequestPayload : undefined,
  );

  const checkIsEmptyDataAndAutoChangeUnit = () => {
    if (
      marketAnalyticsTableResp.isFetching ||
      tradeDataUnitByFilter.isUserClickBack(unitFilter as KeyFilterChartTradeDataUnitBy)
    )
      return;
    const isEmptyData =
      isEmpty(marketAnalyticsTableResp.dataMapping) ||
      every(marketAnalyticsTableResp.dataMapping, (item) => get(item, unitFilter) === 0);
    if (isEmptyData) {
      setUnitFilter(tradeDataUnitByFilter.next());
    }
  };

  useEffect(() => {
    checkIsEmptyDataAndAutoChangeUnit();
  }, [marketAnalyticsTableResp]);

  const marketAnalyticBuyersResp = useQueryGetTradeDataMarketAnalyticBuyers(
    acceptCreditPayment && marketAnalyticsCompanyRequestPayload?.is_buyer
      ? marketAnalyticsCompanyRequestPayload
      : undefined,
  );
  const marketAnalyticSuppliersResp = useQueryGetTradeDataMarketAnalyticSuppliers(
    acceptCreditPayment && !marketAnalyticsCompanyRequestPayload?.is_buyer
      ? marketAnalyticsCompanyRequestPayload
      : undefined,
  );

  const marketAnalyticsDataChart = getArrayDefault(marketAnalyticsChartResp.dataMapping);
  const marketAnalyticsAIExplanation = marketAnalyticsChartAIResp.data?.data?.ai_report;
  const marketAnalyticsDataTable = getArrayDefault(marketAnalyticsTableResp.dataMapping);
  const marketAnalyticsCompany = marketAnalyticsCompanyRequestPayload?.is_buyer
    ? marketAnalyticBuyersResp
    : marketAnalyticSuppliersResp;

  // const transactionsPaymentStatusResp = useQueryTransactionsPaymentStatus(marketAnalyticsCommonRequestPayload);

  const [shouldShowBillableDataLoading] = useCreditFlow({
    response: marketAnalyticsChartResp.data,
    featureInfo: {
      featureId: LIST_FEATURES.TRADE_DATA_BILL_OF_LADING,
      action: LIST_ACTIONS.VIEW,
    },
    // paymentStatus: transactionsPaymentStatusResp.data?.data?.bol,
    // isCollectingData: transactionsPaymentStatusResp.isFetching,
    acceptCreditPayment,
    onSetAcceptCreditPayment: (value) => setAcceptCreditPayment(value),
  });

  const [shouldShowLoadingPopup] = useCreditFlow({
    response: marketAnalyticsCompany?.data,
    acceptCreditPayment,
    onSetAcceptCreditPayment: (value) => setAcceptCreditPayment(value),
    cbClose: () => setIsModalBuySupplyDetailListOpen(false),
  });

  const marketAnalyticsWorldMap = useMemo(() => {
    return getArrayDefault(marketAnalyticsWorldMapResp.dataMapping).map((item) => {
      return [
        item.country_code?.toLowerCase(),
        item?.[chartFieldNameByUnitFilter as keyof IMarketAnalyticsWorldMap],
      ] as [string, number];
    });
  }, [marketAnalyticsWorldMapResp.dataMapping, chartFieldNameByUnitFilter]);

  const [isModalBuySupplyDetailListOpen, setIsModalBuySupplyDetailListOpen] = useState(false);
  const lineChartYData = useMemo(() => {
    return marketAnalyticsDataChart.map((item: IMarketAnalytics) => [
      item?.[chartFieldNameByUnitFilter as keyof IMarketAnalytics],
    ]);
  }, [marketAnalyticsDataChart, chartFieldNameByUnitFilter]);

  const lineChartXData = useMemo(() => {
    return marketAnalyticsDataChart.map((item: IMarketAnalytics) =>
      dayjs(item?.date, FORMAT_DATE_TIME.yyyy_mm).format(FORMAT_DATE_TIME.MONTH_YEAR),
    );
  }, [marketAnalyticsDataChart]);

  useEffect(() => {
    setMarketAnalyticsCompanyRequestPayload((value) => {
      if (!value) return value;

      return {
        ...value,
        offset: marketAnalyticsCompanyTablePaging.page,
        limit: marketAnalyticsCompanyTablePaging.page_size,
        sort_by: marketAnalyticsPopupTableSortParams?.sortBy,
        order: marketAnalyticsPopupTableSortParams?.sort,
      };
    });
  }, [marketAnalyticsCompanyTablePaging, marketAnalyticsPopupTableSortParams]);

  const renderTitle = () => {
    switch (detailTableViewType) {
      case TypeBuyerSupplier.buyer:
        return t('market_analytics.table.column_name.number_of_buyers');
      case TypeBuyerSupplier.supplier:
        return t('market_analytics.table.column_name.number_of_suppliers');
    }
  };

  const configMarketAnalyticsTableColumns: ConfigCell<IMarketAnalytics>[] = [
    {
      disablePadding: false,
      disableSort: true,
      id: 'month',
      label: t('market_analytics.table.column_name.month'),
      width: 150,
      cell: (data: IMarketAnalytics) => (
        <Typography variant="body2" color="text.secondary">
          {dayjs(data.date, FORMAT_DATE_TIME.yyyy_mm).format(FORMAT_DATE_TIME.MONTH_YEAR)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'bol',
      label: t('market_analytics.table.column_name.no_of_bl'),
      cell: (data: IMarketAnalytics) => (
        <Typography variant="body2" color="text.secondary">
          {renderNumberOrDefault(data.number_of_bol)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'weight',
      label: t('market_analytics.table.column_name.weight'),
      width: 150,
      cell: (data: IMarketAnalytics) => (
        <Typography variant="body2" color="text.secondary">
          {renderValueNumberTrade(data.weight)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'quantity',
      label: t('market_analytics.table.column_name.teu'),
      width: 150,
      cell: (data: IMarketAnalytics) => (
        <Typography variant="body2" color={'text.secondary'}>
          {renderValueNumberTrade(data.quantity)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'value_usd',
      // label: t('market_analytics.table.column_name.value'),
      label: (
        <Stack direction={'row'} gap={1} alignItems={'center'}>
          <Box>{t('market_analytics.table.column_name.value')}</Box>
          <TooltipFormLabel title={t('trade_data.value_usd.disclaimer.tooltips')} />
        </Stack>
      ),
      width: 200,
      cell: (data: IMarketAnalytics) => {
        return (
          <Typography variant="body2" color={'text.secondary'}>
            {formatValueUSD(data.value_usd)}
          </Typography>
        );
      },
    },
    {
      disablePadding: false,
      id: 'suppliers',
      label: t('market_analytics.table.column_name.number_of_suppliers'),
      cell: (data: IMarketAnalytics) => {
        const dateRange = [
          dayjs(data.date, FORMAT_DATE_TIME.yyyy_mm).startOf('month').format(FORMAT_DATE_TIME.yyyy_mm_dd),
          dayjs(data.date, FORMAT_DATE_TIME.yyyy_mm).endOf('month').format(FORMAT_DATE_TIME.yyyy_mm_dd),
        ];
        return (
          <Typography
            onClick={() => {
              setSelectedRecord(data);
              setMarketAnalyticsCompanyRequestPayload({
                keyword: queryParams.keyword,
                offset: marketAnalyticsCompanyTablePaging.page,
                limit: marketAnalyticsCompanyTablePaging.page_size,
                is_buyer: false,
                hs_code: hs_code,
                start_date: dateRange[0],
                end_date: dateRange[1],
                supplier_id: queryParams.supplier_id,
                buyer_id: queryParams.buyer_id,
                exp_country_code: queryParams.exp_country_code,
                imp_country_code: queryParams.imp_country_code,
                sort_by: marketAnalyticsPopupTableSortParams?.sortBy,
                order: marketAnalyticsPopupTableSortParams?.sort,
              });
              setDetailTableViewType(TypeBuyerSupplier.supplier);
              setIsModalBuySupplyDetailListOpen(true);
            }}
            variant="body2"
            color="secondary"
          >
            {renderNumberOrDefault(data.suppliers)}
          </Typography>
        );
      },
    },
    {
      disablePadding: false,
      id: 'buyers',
      label: t('market_analytics.table.column_name.number_of_buyers'),
      cell: (data: IMarketAnalytics) => {
        const dateRange = [
          dayjs(data.date, FORMAT_DATE_TIME.yyyy_mm).startOf('month').format(FORMAT_DATE_TIME.yyyy_mm_dd),
          dayjs(data.date, FORMAT_DATE_TIME.yyyy_mm).endOf('month').format(FORMAT_DATE_TIME.yyyy_mm_dd),
        ];
        return (
          <Typography
            onClick={() => {
              setSelectedRecord(data);
              setMarketAnalyticsCompanyRequestPayload({
                keyword: queryParams.keyword,
                offset: marketAnalyticsCompanyTablePaging.page,
                limit: marketAnalyticsCompanyTablePaging.page_size,
                is_buyer: true,
                hs_code: hs_code,
                start_date: dateRange[0],
                end_date: dateRange[1],
                supplier_id: queryParams.supplier_id,
                buyer_id: queryParams.buyer_id,
                exp_country_code: queryParams.exp_country_code,
                imp_country_code: queryParams.imp_country_code,
                sort_by: marketAnalyticsPopupTableSortParams?.sortBy,
                order: marketAnalyticsPopupTableSortParams?.sort,
              });
              setDetailTableViewType(TypeBuyerSupplier.buyer);
              setIsModalBuySupplyDetailListOpen(true);
            }}
            variant="body2"
            color="secondary"
          >
            {renderNumberOrDefault(data.buyers)}
          </Typography>
        );
      },
    },
  ];

  const configBuySupplyDetailListColumns: ConfigCell<IMarketAnalyticsCompany>[] = [
    {
      disablePadding: false,
      id: 'company_name',
      label: t('market_analytics.table.column_name.company_name'),
      cell: (data: IMarketAnalyticsCompany) => (
        <Stack direction="row" spacing={1}>
          <FlagCountry code={data?.company.country_code || ''} />
          <LinkToCompanyDetail
            id={data?.company.id}
            companyName={data.company_name || ''}
            params={queryParams}
            viewedAsType={detailTableViewType}
          />
        </Stack>
      ),
    },
    {
      disablePadding: false,
      id: 'bol',
      label: t('market_analytics.table.column_name.no_of_bl'),
      cell: (data: IMarketAnalyticsCompany) => (
        <Typography variant="body2" color="text.secondary">
          {renderNumberOrDefault(data.bol)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'weight',
      label: t('market_analytics.table.column_name.weight'),
      cell: (data: IMarketAnalyticsCompany) => (
        <Typography variant="body2" color="text.secondary">
          {renderValueNumberTrade(data.weight)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'quantity',
      label: t('market_analytics.table.column_name.teu'),
      cell: (data: IMarketAnalyticsCompany) => (
        <Typography variant="body2" color={'text.secondary'}>
          {renderValueNumberTrade(data.quantity)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'value_usd',
      // label: t('market_analytics.table.column_name.value'),
      label: (
        <Stack direction={'row'} gap={1} alignItems={'center'}>
          <Box>{t('market_analytics.table.column_name.value')}</Box>
          <TooltipFormLabel title={t('trade_data.value_usd.disclaimer.tooltips')} />
        </Stack>
      ),
      cell: (data: IMarketAnalyticsCompany) => {
        const checkRestrictionValue = checkRestriction({
          countryCode: data?.company?.country_code || '',
          typeData: LIST_RESTRICTION_DATA.VALUES_USD,
        });
        return (
          <Typography variant="body2" color={'text.secondary'}>
            {formatValueUSD(data.value_usd)}
            {checkRestrictionValue && <TooltipTradeDataLimit />}
          </Typography>
        );
      },
    },
  ];

  const renderLineChart = () => {
    if (!marketAnalyticsChartResp.isFetching && isEmpty(marketAnalyticsDataChart)) {
      return (
        <CompanyInfoNoData
          title={t('trade_data.common.notice_no_data.title')}
          content={<>{t('trade_data.common.notice_no_data.sub_title')}</>}
          sxContainer={{
            borderWidth: 0,
          }}
        />
      );
    }

    return (
      <LineChart
        loading={shouldShowBillableDataLoading || marketAnalyticsChartResp.isFetching}
        data={lineChartYData}
        dataPropertyKeys={['y', 'custom.number_of_suppliers', 'custom.number_of_buyers']}
        tooltipFormatter={function (this) {
          return `<b>${dayjs(this.x, FORMAT_DATE_TIME.MONTH_YEAR).format(FORMAT_DATE_TIME.LONG_MONTH_YEAR)}</b><br>
    <span style="color: ${text.secondary}">${chartLabelByUnitFilter}: <b style="color: white">${renderNumberOrDefault(this.y)}</b></span><br>`;
        }}
        categories={lineChartXData}
      />
    );
  };

  if (
    !marketAnalyticsChartResp.isFetching &&
    !marketAnalyticsTableResp.isFetching &&
    !marketAnalyticsWorldMapResp.isFetching &&
    isEmpty(marketAnalyticsDataChart) &&
    isEmpty(marketAnalyticsDataTable) &&
    isEmpty(marketAnalyticsWorldMap)
  ) {
    return (
      <CompanyInfoNoData
        title={t('trade_data.common.notice_no_data.title')}
        content={<>{t('trade_data.common.notice_no_data.sub_title')}</>}
      />
    );
  }

  useEffect(() => {
    if (!isModalBuySupplyDetailListOpen) {
      setMarketAnalyticsCompanyTablePaging({
        page: 0,
        page_size: 10,
      });
    }
  }, [isModalBuySupplyDetailListOpen]);

  return (
    <Stack direction={'column'} gap={3}>
      <WhiteBoxContent display={'flex'} flexDirection={'column'} gap={3}>
        <FilterChart
          onSelectTimeRange={({ fromTime, toTime }) => {
            setSelectedRangeTime({
              fromTime: fromTime,
              toTime: toTime,
            });
          }}
          title={t('market_analytics.graph.market_trade_data.title')}
          onSelectUnitBy={(unit) => {
            setUnitFilter(unit as ExpectedAny);
          }}
          selectedUnitBy={unitFilter}
          selectedRangeTime={selectedRangeTime}
        />
        {renderLineChart()}
      </WhiteBoxContent>
      {/* Show screen "Analyzing data" while  get list done and fetching ai_content */}
      {marketAnalyticsChartAIResp?.isFetching && marketAnalyticsChartResp.isFetched && <AISummaryLoading />}
      {/* Check if all values on AI content is empty => HIDE */}
      {!marketAnalyticsChartAIResp.isFetching &&
        every(Object.values(marketAnalyticsAIExplanation), (content) => !isEmpty(content)) && (
          <WhiteBoxContent
            sx={{
              background: `linear-gradient(90deg, ${alpha(primary.light, 0.2)} 0%,${alpha(primary.light, 0.1)} 100%)`,
            }}
            border={`1px solid ${primary.light}`}
          >
            <Stack alignItems="center" direction="row" gap={3} mb={4}>
              <Stack
                width={spacing(4)}
                height={spacing(4)}
                borderRadius={borderRadius / 2}
                bgcolor={primary.main}
                justifyContent="center"
                alignItems="center"
              >
                <AutoAwesome sx={{ color: background.paper }} />
              </Stack>
              <Typography color="text.primary" variant="h5">
                AI Summary
              </Typography>
            </Stack>
            <Stack direction="column" gap={2}>
              <Stack direction="row" gap={2}>
                <IconFeather icon={<TrendingUp />} muiProps={{ color: 'primary' }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} color="primary.dark">
                    Trend
                  </Typography>
                  <Typography variant="body1">{marketAnalyticsAIExplanation.trend}</Typography>
                </Box>
              </Stack>
              <Stack direction="row" gap={2}>
                <IconFeather icon={<AlertCircle />} muiProps={{ color: 'warning' }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} color="warning.dark">
                    Anomaly
                  </Typography>
                  <Typography variant="body1">{marketAnalyticsAIExplanation.anomaly}</Typography>
                </Box>
              </Stack>
              <Stack direction="row" gap={2}>
                <IconFeather icon={<Calendar />} muiProps={{ color: 'info' }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} color="info.dark">
                    Season
                  </Typography>
                  <Typography variant="body1">{marketAnalyticsAIExplanation.season}</Typography>
                </Box>
              </Stack>
              <Stack direction="row" gap={2}>
                <IconFeather icon={<Target />} muiProps={{ color: 'success' }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} color="success.dark">
                    Action
                  </Typography>
                  <Typography variant="body1">{marketAnalyticsAIExplanation.action}</Typography>
                </Box>
              </Stack>
            </Stack>
          </WhiteBoxContent>
        )}
      <WhiteBoxContent>
        <Typography display={'flex'} justifyContent={'space-between'} textAlign={'left'} variant="h5">
          Market Analytics Data
          <ExportDataTable
            fileName="market-analytics"
            paramsExport={marketAnalyticsTableRequestPayload}
            endpoint={get(URL_EXPORT_MAPPING, LIST_FEATURES.TRADE_DATA_MARKET_ANALYTICS)}
            featureId={LIST_FEATURES.TRADE_DATA_MARKET_ANALYTICS}
            totalRecordsAvailable={marketAnalyticsTableResp?.data?.total_records || 0}
          />
        </Typography>

        <Box mt={3} />
        <Table
          handleSort={(data) => {
            setTableSort('marketAnalyticsTableSortParams', data);
          }}
          onChangePage={(value) => setMarketAnalyticsTablePagingModel({ ...marketAnalyticsTablePaging, page: value })}
          onChangePageSize={(value) => setMarketAnalyticsTablePagingModel({ page: 0, page_size: value })}
          loading={shouldShowBillableDataLoading || marketAnalyticsTableResp.isFetching}
          page={marketAnalyticsTablePaging.page}
          pageSize={marketAnalyticsTablePaging.page_size}
          totalRecords={marketAnalyticsTableResp.data?.total_records}
          dataKey={'date'}
          data={marketAnalyticsDataTable}
          configColumn={configMarketAnalyticsTableColumns}
          maxRow={TABLE_PAGE_SIZE_12}
        />
      </WhiteBoxContent>
      {isViewingGlobalData && (
        <WhiteBoxContent>
          <Stack mb={3} justifyContent={'space-between'} direction={'row'}>
            <Typography textAlign={'left'} variant="h5">
              {t('market_analytics.graph.product_export_map.title')}
            </Typography>
          </Stack>
          {
            <WorldMapChart
              tooltipFormatter={function () {
                const point: Point = this.point;
                const flag = getEmojiFlag(point.properties['hc-key']);
                return `<div>
            <b>${flag} ${point.name}</b>
            <br/><b style="color: ${text.secondary}">${chartLabelByUnitFilter} </b> <b>${renderNumberOrDefault(point.value)}</b>
          </div>`;
              }}
              data={marketAnalyticsWorldMap}
              height={580}
              type={WorldMapOptions.customer}
            />
          }
        </WhiteBoxContent>
      )}
      <ModalComponent
        padding={0}
        pt={0}
        handleClose={() => setIsModalBuySupplyDetailListOpen(false)}
        open={isModalBuySupplyDetailListOpen}
      >
        <Stack direction="column" spacing={3} width="80vw" bgcolor={background.paper} p={4}>
          <Stack alignItems={'center'} direction="row" justifyContent="space-between">
            <Typography variant="h5">{renderTitle()}</Typography>
            <Typography variant="body2" color="text.secondary">
              This is a list of {detailTableViewType === TypeBuyerSupplier.supplier ? 'suppliers' : 'buyers'} with HS
              code {hs_code} from {dayjs(start_date).format(FORMAT_DATE_TIME.LONG_MONTH_DAY_YEAR)} until{' '}
              {dayjs(end_date).format(FORMAT_DATE_TIME.LONG_MONTH_DAY_YEAR)}
            </Typography>
            <ExportDataTable
              fileName={
                detailTableViewType === TypeBuyerSupplier.supplier
                  ? 'market-analytics-suppliers'
                  : 'market-analytics-buyers'
              }
              paramsExport={marketAnalyticsCompanyRequestPayload}
              featureId={LIST_FEATURES.INTERNAL_TRADE_DATA_SUB_LIST_SUPPLIERS_BUYERS}
              endpoint={
                detailTableViewType === TypeBuyerSupplier.supplier
                  ? get(URL_EXPORT_MAPPING, EXPORT_TABLE_TYPE.TRADE_DATA_MARKET_ANALYTICS_SUPPLIER_LIST)
                  : get(URL_EXPORT_MAPPING, EXPORT_TABLE_TYPE.TRADE_DATA_MARKET_ANALYTICS_BUYER_LIST)
              }
              totalRecordsAvailable={marketAnalyticsCompany?.data?.total_records || 0}
            />
          </Stack>

          {isEmpty(getArrayDefault(marketAnalyticsCompany.data.data)) && !marketAnalyticsCompany.isFetching ? (
            <Alert severity="info" color="info">
              Withheld by Government
            </Alert>
          ) : (
            <Table
              maxRow={10}
              onChangePage={(value) =>
                setMarketAnalyticsCompanyTablePaging({ ...marketAnalyticsCompanyTablePaging, page: value })
              }
              handleSort={(data) => {
                setPopupTableSort('marketAnalyticsPopupTableSortParams', data);
              }}
              onChangePageSize={(value) => setMarketAnalyticsCompanyTablePaging({ page: 0, page_size: value })}
              loading={shouldShowLoadingPopup || marketAnalyticsCompany.isFetching}
              page={marketAnalyticsCompanyTablePaging.page}
              pageSize={marketAnalyticsCompanyTablePaging.page_size}
              totalRecords={marketAnalyticsCompany.data.total_records}
              dataKey={(item) => item.company?.id}
              data={getArrayDefault(marketAnalyticsCompany.data.data)}
              configColumn={configBuySupplyDetailListColumns}
              pagingTooltipsText={
                (selectedRecord?.[detailTableViewType === TypeBuyerSupplier.buyer ? 'buyers' : 'suppliers'] || 0) >
                (marketAnalyticsCompany.data?.total_records || 9999999)
                  ? 'trade_data.count_mismatch.tooltip'
                  : undefined
              }
            />
          )}
        </Stack>
      </ModalComponent>
    </Stack>
  );
};

export default MarketAnalytics;
